import { Outlet, NavLink, useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Breadcrumb } from "../components/Topbar";
import { Badge } from "../components/Badge";
import { useTenant } from "../hooks/useTenant";
import { useViewport } from "../hooks/useViewport";
import { formatDate, daysSince } from "../lib/format";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";
import { EyeIcon, MoreIcon } from "../components/icons";

const TABS = [
  { to: "",                label: "Visão",                end: true },
  { to: "branding",        label: "Branding" },
  { to: "vocabulary",      label: "Vocabulário" },
  { to: "activity-types",  label: "Tipos de atividade" },
  { to: "benefit-types",   label: "Tipos de benefício" },
  { to: "app",             label: "App" },
  { to: "features",        label: "Features" },
  { to: "billing",         label: "Faturamento" },
  { to: "log",             label: "Log" },
] as const;

export function TenantDetailLayout() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, loading, error } = useTenant(slug);
  const vp = useViewport();
  const isMobile = vp === "mobile";

  if (loading) {
    return (
      <Layout breadcrumbs={<Breadcrumb items={[{ label: "Tenants", to: "/tenants" }, { label: "..." }]} />}>
        <div style={{ color: colors.stone }}>Carregando...</div>
      </Layout>
    );
  }
  if (error || !tenant) {
    return (
      <Layout breadcrumbs={<Breadcrumb items={[{ label: "Tenants", to: "/tenants" }, { label: "Erro" }]} />}>
        <div style={{ color: colors.error }}>Tenant não encontrado.</div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={<Breadcrumb items={[{ label: "Tenants", to: "/tenants" }, { label: tenant.name }]} />}>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-start", justifyContent: "space-between", gap: isMobile ? 16 : 24, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0 }}>
          <div style={{ width: 56, height: 56, flexShrink: 0, borderRadius: radius.md, background: colors.graphite, color: colors.paper, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: fontWeight.medium, fontSize: 22, letterSpacing: "-0.02em" }}>
            {tenant.name[0]}
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: fontWeight.medium, letterSpacing: "-0.018em", lineHeight: 1.15 }}>{tenant.name}</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: fontSize.md, color: colors.stone, marginTop: 4 }}>
              <Badge state={tenant.lifecycle_state as never} />
              <span style={{ width: 3, height: 3, borderRadius: "50%", background: colors.stoneSoft }} />
              <code style={{ fontFamily: "JetBrains Mono", fontSize: 12 }}>{tenant.slug}</code>
              {tenant.legal_name && (<><span style={{ width: 3, height: 3, borderRadius: "50%", background: colors.stoneSoft }} /><span>{tenant.legal_name}</span></>)}
              <span style={{ width: 3, height: 3, borderRadius: "50%", background: colors.stoneSoft }} />
              <span>desde {formatDate(tenant.created_at)} · {daysSince(tenant.created_at)}d</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: space.sm, flexWrap: "wrap" }}>
          <button onClick={() => alert("Impersonation chega na Phase 3")}
                  style={{ height: 36, padding: "0 14px", background: colors.paper, color: colors.ink, border: `1px solid ${colors.fog}`, borderRadius: radius.sm, fontSize: fontSize.md, fontWeight: fontWeight.medium, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <EyeIcon width={14} height={14} /> Visualizar como staff
          </button>
          <button style={{ width: 32, height: 32, padding: 0, background: colors.paper, color: colors.ink, border: `1px solid ${colors.fog}`, borderRadius: radius.sm, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <MoreIcon width={14} height={14} />
          </button>
        </div>
      </div>

      <nav style={{ display: "flex", gap: 4, borderBottom: `1px solid ${colors.fog}`, marginBottom: 24, overflowX: "auto" }}>
        {TABS.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={"end" in t ? t.end : undefined}
            style={({ isActive }) => ({
              padding: "12px 14px",
              fontSize: fontSize.md,
              fontWeight: fontWeight.medium,
              color: isActive ? colors.ink : colors.stone,
              textDecoration: "none",
              borderBottom: `2px solid ${isActive ? colors.ink : "transparent"}`,
              marginBottom: -1,
              whiteSpace: "nowrap",
              userSelect: "none",
            })}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </Layout>
  );
}
